import { useId, useState } from "react";
import { useFormik } from "formik";
import { Button, Input, Label } from "@/components/ui";
import { usePermission, useToast } from "@/hooks";
import { ModelType } from "@/types";
import { DisplayError } from "@/utils";
import { TypeModelsValidationSchema } from "@/shared";

type Values = { name: string; entity_type: ModelType["entity_type"] | "" };
export function TypeModelFields({ typeModel, save, isLoading, handleClose, showSuccessToast = true }: {
  typeModel?: ModelType;
  showSuccessToast?: boolean;
  save: (values: Values) => Promise<unknown>;
  isLoading: boolean;
  handleClose: () => void;
}) {
  const id = useId();
  const { toast } = useToast();
  const { checkPermission } = usePermission();
  const [error, setError] = useState("");
  const canSave = checkPermission("modelstypes", typeModel ? "edit modelstypes" : "create modelstypes");
  const formik = useFormik<Values>({
    initialValues: { name: typeModel?.name || "", entity_type: typeModel?.entity_type || "" },
    validationSchema: TypeModelsValidationSchema(),
    onSubmit: async values => {
      if (!canSave || isLoading) return;
      setError("");
      try {
        await save(values);
        if (showSuccessToast) toast({ title: "Sucesso", description: typeModel ? "Tipo de modelo atualizado." : "Tipo de modelo criado." });
        handleClose();
      } catch (cause) {
        const message = DisplayError(cause, "Não foi possível salvar o tipo de modelo.");
        setError(message);
        toast({ title: "Erro", description: message, variant: "destructive" });
      }
    },
  });
  return <form onSubmit={event => { event.stopPropagation(); formik.handleSubmit(event); }} className="space-y-5">
    <div className="space-y-2">
      <Label htmlFor={`${id}-name`}>Nome do tipo</Label>
      <Input id={`${id}-name`} {...formik.getFieldProps("name")} isInvalid={formik.touched.name && formik.errors.name} messageError={formik.errors.name} />
    </div>
    <div className="space-y-2">
      <Label htmlFor={`${id}-entity`}>Destinatário</Label>
      <select id={`${id}-entity`} {...formik.getFieldProps("entity_type")} className="h-11 w-full rounded-md border border-app-border bg-app-surface px-3 text-sm focus:ring-2 focus:ring-brand" aria-invalid={!!(formik.touched.entity_type && formik.errors.entity_type)} aria-describedby={`${id}-entity-error`}>
        <option value="">Selecione o destinatário</option><option value="patient">Paciente</option><option value="employee">Colaborador</option><option value="both">Paciente e colaborador</option>
      </select>
      {formik.values.entity_type === "both" && <p className="text-sm text-app-fg-muted">Modelo reutilizável para um paciente ou um colaborador, gerado para uma pessoa por vez.</p>}
      {formik.touched.entity_type && formik.errors.entity_type && <p id={`${id}-entity-error`} className="text-sm text-app-danger">{formik.errors.entity_type}</p>}
    </div>
    {error && <p role="alert" className="text-sm text-app-danger">{error}</p>}
    <div className="flex justify-end gap-2 border-t border-app-border pt-4">
      <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>Cancelar</Button>
      {canSave && <Button type="submit" variant="petrol" disabled={isLoading} isLoading={isLoading}>Salvar tipo</Button>}
    </div>
  </form>;
}
